import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getPurchaseThisMonth,
  type PurchaseOutOfPlan,
  type PurchasePlanGoodsRow,
  type PurchasePlanItemGroup,
  type PurchaseThisMonthCategorySummary,
  type PurchaseThisMonthData,
  type PurchaseThisMonthSummary,
} from '@/api/purchase';
import UsageRatioBar from '@/components/base/UsageRatioBar';

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function money(v: unknown): string {
  return `¥${n(v).toFixed(2)}`;
}

type UsageRatioSort = 'none' | 'asc' | 'desc';

function cycleUsageRatioSort(current: UsageRatioSort): UsageRatioSort {
  if (current === 'none') return 'desc';
  if (current === 'desc') return 'asc';
  return 'none';
}

function sortRowsByUsageRatio(
  rows: PurchasePlanGoodsRow[],
  sort: UsageRatioSort,
): PurchasePlanGoodsRow[] {
  if (sort === 'none' || rows.length <= 1) return rows;
  const next = [...rows];
  next.sort((a, b) => {
    const ra = n(a.usage_ratio);
    const rb = n(b.usage_ratio);
    return sort === 'asc' ? ra - rb : rb - ra;
  });
  return next;
}

function SummaryCard({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: 'primary' | 'accent' | 'purple';
}) {
  const valueClass =
    emphasize === 'primary'
      ? 'text-primary-600'
      : emphasize === 'accent'
        ? 'text-accent-600'
        : emphasize === 'purple'
          ? 'text-purple-600'
          : 'text-foreground-900';
  const labelClass = emphasize === 'purple' ? 'text-purple-500' : 'text-foreground-500';
  return (
    <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5">
      <p className={`text-xs mb-1 ${labelClass}`}>{label}</p>
      <p className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value}</p>
    </div>
  );
}

function GroupTable({
  group,
  usageRatioSort,
  onToggleSort,
}: {
  group: PurchasePlanItemGroup;
  usageRatioSort: UsageRatioSort;
  onToggleSort: () => void;
}) {
  const rawItems = Array.isArray(group.items) ? group.items : [];
  const items = sortRowsByUsageRatio(rawItems, usageRatioSort);
  const sortIcon =
    usageRatioSort === 'asc'
      ? 'ri-sort-asc'
      : usageRatioSort === 'desc'
        ? 'ri-sort-desc'
        : 'ri-arrow-up-down-line';

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200">
      <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
        <thead>
          <tr className="bg-background-50">
            <th className="py-2.5 px-2 text-center text-xs font-semibold text-foreground-600 w-10 whitespace-nowrap">
              序号
            </th>
            {Number(group.control_type) !== 1 && (
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                科室名称
              </th>
            )}
            {Number(group.control_type) === 3 && (
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                人员姓名
              </th>
            )}
            <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
              物资名称
            </th>
            <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
              规格名称
            </th>
            <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
              编码
            </th>
            <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
              单位
            </th>
            <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
              计划数
            </th>
            <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
              已使用
            </th>
            <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
              已退货
            </th>
            <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
              剩余可用
            </th>
            <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
              计划金额
            </th>
            <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
              已使用金额
            </th>
            <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
              剩余金额
            </th>
            <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
              <button
                type="button"
                onClick={onToggleSort}
                className={`inline-flex items-center gap-1 transition-colors ${
                  usageRatioSort === 'none'
                    ? 'text-foreground-600 hover:text-primary-600'
                    : 'text-primary-600'
                }`}
              >
                已使用比例
                <i className={`${sortIcon} text-sm`} />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((g, idx) => {
            const specLabel =
              Number(g.spec_id) > 0
                ? g.spec_name || g.spec?.name || '-'
                : '不限定规格';
            return (
              <tr
                key={String(g.flow_item_id ?? `${g.spec_id}-${idx}`)}
                className="hover:bg-background-50/60 transition-colors"
              >
                <td className="py-2.5 px-2 text-center text-foreground-500 text-xs">{idx + 1}</td>
                {Number(group.control_type) !== 1 && (
                  <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">
                    {Number(g.department_id) > 0 ? g.department_name || '-' : '全院共享'}
                  </td>
                )}
                {Number(group.control_type) === 3 && (
                  <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">
                    {g.account_name || '-'}
                  </td>
                )}
                <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[140px] truncate">
                  {g.goods_name || g.goods?.title || '-'}
                </td>
                <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">
                  {specLabel}
                </td>
                <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">
                  {g.code || g.spec?.sku || '-'}
                </td>
                <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">
                  {g.unit || g.spec?.unit || '-'}
                </td>
                <td className="py-2.5 px-3 text-center text-foreground-700 text-xs font-medium whitespace-nowrap">
                  {n(g.plan_quantity)}
                </td>
                <td className="py-2.5 px-3 text-center text-foreground-700 text-xs whitespace-nowrap">
                  {n(g.used_quantity ?? g.sale_quantity)}
                </td>
                <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">
                  {n(g.return_quantity)}
                </td>
                <td className="py-2.5 px-3 text-center text-foreground-800 text-xs font-medium whitespace-nowrap">
                  {n(g.remain_quantity ?? g.limit_quantity)}
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                  {money(g.plan_amount)}
                </td>
                <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                  {money(g.used_amount)}
                </td>
                <td className="py-2.5 px-3 text-right text-primary-600 text-xs font-semibold whitespace-nowrap">
                  {money(g.remain_amount)}
                </td>
                <td className="py-2.5 px-3 text-left">
                  <UsageRatioBar ratio={n(g.usage_ratio)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PurchasePlanThisMonthPage() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState<PurchaseThisMonthData | null>(null);
  const [usageRatioSort, setUsageRatioSort] = useState<UsageRatioSort>('none');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPurchaseThisMonth();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok || !res.data) {
        setData(null);
        setLoadError(res.message || res.msg || '加载本月采购计划失败');
        return;
      }
      setData(res.data);
    } catch {
      setData(null);
      setLoadError('网络异常，加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const summary: PurchaseThisMonthSummary = data?.summary || {};
  const categories: PurchaseThisMonthCategorySummary[] = Array.isArray(data?.category_summary)
    ? data!.category_summary!
    : [];
  const groups: PurchasePlanItemGroup[] = Array.isArray(data?.groups) ? data!.groups! : [];
  const outOfPlan: PurchaseOutOfPlan = data?.out_of_plan || {};
  const outSummary = outOfPlan.summary || {};
  const outCategories = Array.isArray(outOfPlan.category_summary) ? outOfPlan.category_summary : [];
  const outItems = Array.isArray(outOfPlan.items) ? outOfPlan.items : [];

  const monthLabel = data?.apply_month_text || '本月';
  const title = useMemo(() => {
    const hasEnterprise = groups.some((g) => Number(g.control_type) === 1);
    const hasDeptScope = groups.some((g) => {
      const type = Number(g.control_type);
      return type === 2 || type === 3;
    });
    const deptName = (data?.department_name || '').trim();
    if (hasEnterprise && hasDeptScope && deptName) {
      return `${monthLabel}计划 全院共享 · ${deptName}`;
    }
    if (hasEnterprise) return `${monthLabel}计划 全院共享`;
    if (deptName) return `${deptName} · ${monthLabel}计划`;
    return `${monthLabel}计划`;
  }, [data?.department_name, groups, monthLabel]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-24 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin mr-2" />
        加载中...
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-24 px-4">
        <i className="ri-error-warning-line text-3xl text-primary-500 mb-3" />
        <p className="text-sm text-foreground-600 mb-4">{loadError || '暂无数据'}</p>
        <button
          type="button"
          onClick={() => void fetchData()}
          className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50"
        >
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6 space-y-4">
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 md:px-5 py-3 border-b border-background-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground-900 truncate">{title}</h1>
            </div>
            <button
              type="button"
              onClick={() => void fetchData()}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line text-xs" />
              刷新
            </button>
          </div>

          <div className="p-4 md:p-5 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
              <SummaryCard label="计划项数" value={String(n(summary.item_count))} />
              <SummaryCard label="计划数量" value={String(n(summary.plan_quantity))} />
              <SummaryCard label="已使用数量" value={String(n(summary.used_quantity))} />
              <SummaryCard label="剩余可用" value={String(n(summary.remain_quantity))} emphasize="primary" />
              <SummaryCard label="计划金额" value={money(summary.plan_amount)} />
              <SummaryCard label="已使用金额" value={money(summary.used_amount)} />
              <SummaryCard label="退货金额" value={money(summary.return_amount)} />
              <SummaryCard label="剩余金额" value={money(summary.remain_amount)} emphasize="primary" />
              <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5 col-span-2 md:col-span-1">
                <p className="text-xs text-foreground-500 mb-1.5">已使用比例</p>
                <UsageRatioBar ratio={n(summary.usage_ratio)} />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <SummaryCard
                label="计划外物资数"
                value={String(n(outSummary.goods_count))}
                emphasize="purple"
              />
              <SummaryCard
                label="计划外数量"
                value={String(n(outSummary.quantity))}
                emphasize="purple"
              />
              <SummaryCard
                label="计划外金额"
                value={money(outSummary.amount)}
                emphasize="purple"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">分类汇总</h2>
            <span className="text-xs text-foreground-500">
              计划 {categories.length} 类
              {outCategories.length > 0 ? ` · 计划外 ${outCategories.length} 类` : ''}
            </span>
          </div>
          {categories.length === 0 && outCategories.length === 0 ? (
            <p className="py-10 text-center text-sm text-foreground-400">暂无分类汇总</p>
          ) : (
            <div className="p-4 space-y-4">
              {categories.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-foreground-700 mb-2">计划分类</h3>
                  <div className="overflow-x-auto rounded-lg border border-background-200">
                    <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                      <thead>
                        <tr className="bg-background-50">
                          <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600">分类</th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">项数</th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">计划数</th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">已使用</th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">剩余</th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">计划金额</th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">已使用金额</th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">剩余金额</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">已使用比例</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((row) => (
                          <tr key={String(row.category_id ?? row.category_name)} className="hover:bg-background-50/60">
                            <td className="py-2 px-3 text-xs text-foreground-800">{row.category_name || '未分类'}</td>
                            <td className="py-2 px-3 text-center text-xs text-foreground-700">{n(row.item_count)}</td>
                            <td className="py-2 px-3 text-center text-xs text-foreground-700">{n(row.plan_quantity)}</td>
                            <td className="py-2 px-3 text-center text-xs text-foreground-700">{n(row.used_quantity)}</td>
                            <td className="py-2 px-3 text-center text-xs text-foreground-800 font-medium">{n(row.remain_quantity)}</td>
                            <td className="py-2 px-3 text-right text-xs text-foreground-700 whitespace-nowrap">{money(row.plan_amount)}</td>
                            <td className="py-2 px-3 text-right text-xs text-foreground-700 whitespace-nowrap">{money(row.used_amount)}</td>
                            <td className="py-2 px-3 text-right text-xs text-primary-600 font-semibold whitespace-nowrap">{money(row.remain_amount)}</td>
                            <td className="py-2 px-3 text-left">
                              <UsageRatioBar ratio={n(row.usage_ratio)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-foreground-400">暂无计划分类汇总</p>
              )}

              {outCategories.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-purple-600 mb-2">计划外分类</h3>
                  <div className="overflow-x-auto rounded-lg border border-purple-200">
                    <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-purple-200 [&_td]:border [&_td]:border-purple-100">
                      <thead>
                        <tr className="bg-purple-50">
                          <th className="py-2 px-3 text-left text-xs font-semibold text-purple-700">分类</th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-purple-700 whitespace-nowrap">
                            物资数
                          </th>
                          <th className="py-2 px-3 text-center text-xs font-semibold text-purple-700 whitespace-nowrap">
                            数量
                          </th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-purple-700 whitespace-nowrap">
                            金额
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {outCategories.map((row) => (
                          <tr
                            key={`out-${String(row.category_id ?? row.category_name)}`}
                            className="hover:bg-purple-50/60"
                          >
                            <td className="py-2 px-3 text-xs text-purple-800">
                              {row.category_name || '未分类'}
                            </td>
                            <td className="py-2 px-3 text-center text-xs text-purple-700">
                              {n(row.goods_count)}
                            </td>
                            <td className="py-2 px-3 text-center text-xs text-purple-700">
                              {n(row.quantity)}
                            </td>
                            <td className="py-2 px-3 text-right text-xs text-purple-600 font-semibold whitespace-nowrap">
                              {money(row.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">计划明细</h2>
            <span className="text-xs text-foreground-500">
              {groups.length} 组 · 共 {n(summary.item_count)} 项
            </span>
          </div>
          {groups.length === 0 ? (
            <p className="py-12 text-center text-sm text-foreground-400">本月暂无适用的采购计划明细</p>
          ) : (
            <div className="divide-y divide-background-100">
              {groups.map((group) => {
                const items = Array.isArray(group.items) ? group.items : [];
                const groupUsage = n(group.usage_ratio);
                return (
                  <div key={`group-${group.control_type}`} className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex items-center rounded bg-background-100 px-2 py-0.5 text-xs font-semibold text-foreground-800">
                          {Number(group.control_type) === 1
                            ? `${monthLabel}计划 全院共享`
                            : group.control_type_text || `类型${group.control_type}`}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {group.item_count ?? items.length} 项
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-600">
                        <span>
                          计划数 <b className="text-foreground-900">{n(group.plan_quantity)}</b>
                        </span>
                        <span>
                          计划金额{' '}
                          <b className="text-foreground-900">{money(group.plan_amount)}</b>
                        </span>
                        <span>
                          剩余{' '}
                          <b className="text-primary-600">{money(group.remain_amount)}</b>
                        </span>
                        <UsageRatioBar ratio={groupUsage} />
                      </div>
                    </div>
                    {items.length === 0 ? (
                      <p className="py-6 text-center text-xs text-foreground-400">本组暂无明细</p>
                    ) : (
                      <GroupTable
                        group={group}
                        usageRatioSort={usageRatioSort}
                        onToggleSort={() => setUsageRatioSort((s) => cycleUsageRatioSort(s))}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-purple-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-purple-100 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-purple-700">本月采购计划外情况</h2>
            <span className="text-xs text-purple-600">
              {n(outSummary.goods_count)} 种物资 · {n(outSummary.quantity)} 件 · {money(outSummary.amount)}
            </span>
          </div>

          {outItems.length === 0 ? (
            <p className="py-12 text-center text-sm text-purple-400">本月暂无计划外采购</p>
          ) : (
            <div className="p-4">
              <div className="overflow-x-auto rounded-lg border border-purple-200">
                <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-purple-200 [&_td]:border [&_td]:border-purple-100">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-purple-700 w-10 whitespace-nowrap">
                        序号
                      </th>
                      <th className="py-2.5 px-3 text-left text-xs font-semibold text-purple-700 whitespace-nowrap">
                        物资名称
                      </th>
                      <th className="py-2.5 px-3 text-left text-xs font-semibold text-purple-700 whitespace-nowrap">
                        规格名称
                      </th>
                      <th className="py-2.5 px-3 text-left text-xs font-semibold text-purple-700 whitespace-nowrap">
                        编码
                      </th>
                      <th className="py-2.5 px-3 text-center text-xs font-semibold text-purple-700 whitespace-nowrap">
                        单位
                      </th>
                      <th className="py-2.5 px-3 text-center text-xs font-semibold text-purple-700 whitespace-nowrap">
                        数量
                      </th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-purple-700 whitespace-nowrap">
                        单价
                      </th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-purple-700 whitespace-nowrap">
                        金额
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {outItems.map((row, idx) => (
                      <tr
                        key={`${row.goods_id}-${row.spec_id}-${idx}`}
                        className="hover:bg-purple-50/60 transition-colors"
                      >
                        <td className="py-2.5 px-2 text-center text-purple-400 text-xs">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3 text-purple-800 text-xs max-w-[160px] truncate">
                          {row.goods_name || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-purple-700 text-xs max-w-[140px] truncate">
                          {Number(row.spec_id) > 0 ? row.spec_name || '-' : '不限定规格'}
                        </td>
                        <td className="py-2.5 px-3 text-purple-500 text-xs whitespace-nowrap">
                          {row.code || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center text-purple-700 text-xs whitespace-nowrap">
                          {row.unit || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center text-purple-800 text-xs font-medium whitespace-nowrap">
                          {n(row.quantity)}
                        </td>
                        <td className="py-2.5 px-3 text-right text-purple-700 text-xs whitespace-nowrap">
                          {money(row.price)}
                        </td>
                        <td className="py-2.5 px-3 text-right text-purple-600 text-xs font-semibold whitespace-nowrap">
                          {money(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
